import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Spin, Divider, message, Input, Select } from 'antd';
import { 
  ArrowLeftOutlined, 
  DesktopOutlined, 
  InfoCircleOutlined, 
  DashboardOutlined, 
  ClockCircleOutlined, 
  ReloadOutlined, 
  PoweroffOutlined, 
  EditOutlined, 
  CloudUploadOutlined, 
  SettingOutlined, 
  AppstoreOutlined,
  HistoryOutlined,
  OrderedListOutlined // DODATA IKONICA ZA PLEJLISTU
} from '@ant-design/icons';
import axios from 'axios';
import EditBoxModal from './EditBoxModal';

const { Option } = Select;

axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

const ScreenDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [box, setBox] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [isNameModalVisible, setIsNameModalVisible] = useState(false);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  const [editData, setEditData] = useState({ 
    box_name: '', 
    location: '',
    resolution: '1920x1080',
    rotation: '0'
  });

  const fetchBoxDetails = async () => {
    try {
      const response = await axios.get('/api/box/list/');
      const selectedBox = response.data.find(b => b.id.toString() === id);
      
      if (selectedBox) {
        setBox(selectedBox);
        if (!isNameModalVisible && !isSettingsModalVisible) {
          setEditData({ 
            box_name: selectedBox.box_name || '', 
            location: selectedBox.location || '',
            resolution: selectedBox.resolution || '1920x1080',
            rotation: selectedBox.rotation || '0'
          });
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading box details:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoxDetails();
    const interval = setInterval(() => {
      if (!isNameModalVisible && !isSettingsModalVisible) fetchBoxDetails();
    }, 10000);
    return () => clearInterval(interval);
  }, [id, isNameModalVisible, isSettingsModalVisible]);

  const handleSave = async () => {
    try {
      setUpdating(true);
      message.loading({ content: 'Saving changes...', key: 'save' });
      
      await axios.post('/api/box/rename-box/', {
        box_id: box.box_id,
        box_name: editData.box_name,
        location: editData.location
      });

      await axios.post('/api/box/update-settings/', {
        box_id: box.box_id,
        resolution: editData.resolution,
        rotation: editData.rotation
      });

      message.success({ content: 'Changes saved and commands sent!', key: 'save', duration: 3 });
      setIsNameModalVisible(false);
      setIsSettingsModalVisible(false);
      fetchBoxDetails(); 
    } catch (error) {
      console.error('Save error:', error);
      message.error({ content: 'Error saving: ' + (error.response?.data?.message || 'Server error'), key: 'save' });
    } finally {
      setUpdating(false);
    }
  };

  const getTempColor = (tempStr) => {
    const temp = parseFloat(tempStr);
    return temp > 75 ? '#ef4444' : '#34d399';
  };

  const getLoadColor = (loadStr) => {
    const load = parseFloat(loadStr);
    return load > 80 ? '#ef4444' : '#34d399';
  };

  const getRamColor = (ramStr) => {
    if (!ramStr) return '#34d399';
    const usedRam = parseInt(ramStr.split('/')[0]);
    return usedRam > 3200 ? '#ef4444' : '#34d399';
  };

  const getDiskColor = (diskStr) => {
    if (!diskStr) return '#34d399';
    const usedDisk = parseInt(diskStr.split('/')[0]);
    return usedDisk > 22 ? '#ef4444' : '#34d399';
  };

  const handleAction = async (actionType) => {
    try {
      message.loading({ content: `Sending command: ${actionType}...`, key: 'action' });
      await axios.post(`/api/box/control/`, { box_id: box.box_id, action: actionType });
      message.success({ content: `Command ${actionType} sent!`, key: 'action', duration: 3 });
    } catch (error) {
      message.error({ content: 'Error sending command', key: 'action' });
    }
  };

  const formatLastSeen = (dateStr) => {
    if (!dateStr || dateStr === "-") return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('sr-RS', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
    });
  };

  if (loading) return <div style={{ padding: '100px', textAlign: 'center' }}><Spin size="large" /></div>;
  if (!box) return <div style={{ color: 'white', padding: '50px' }}>Box not found.</div>;

  const cardStyle = { background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', height: '320px' };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/')}
          style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}
        >
          Back
        </Button>
        <h2 style={{ color: 'white', margin: 0 }}>
          <DesktopOutlined style={{ marginRight: '10px' }} />
          {box.box_name || box.box_id}
        </h2>
      </div>

      <Card title={<span style={{ color: 'white' }}>Quick Actions</span>} style={{ background: '#1e293b', border: '1px solid #334155', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button icon={<ReloadOutlined />} onClick={() => handleAction('restart_player')} style={{ background: '#0f172a', color: '#60a5fa', border: '1px solid #334155' }}>Restart Player</Button>
          <Button icon={<PoweroffOutlined />} onClick={() => handleAction('restart_box')} style={{ background: '#0f172a', color: '#f87171', border: '1px solid #334155' }}>Reboot Box</Button>
          <Button icon={<CloudUploadOutlined />} onClick={() => handleAction('update_apk')} style={{ background: '#0f172a', color: '#34d399', border: '1px solid #334155' }}>Update APK</Button>
        </div>
      </Card>

      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card title={<span style={{ color: 'white' }}><InfoCircleOutlined /> Info & Network</span>} bordered={false} style={cardStyle}>
            <div className="detail-item edit-hover" onClick={() => setIsNameModalVisible(true)}>
              <span className="label">NAME</span>
              <span className="value" style={{ color: '#60a5fa' }}>{box.box_name || '-'} <EditOutlined style={{ fontSize: '10px' }} /></span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item"><span className="label">BOX ID</span><span className="value">{box.box_id}</span></div>
            <Divider className="card-divider" />
            <div className="detail-item edit-hover" onClick={() => setIsNameModalVisible(true)}>
              <span className="label">LOCATION</span>
              <span className="value">{box.location || '-'} <EditOutlined style={{ fontSize: '10px' }} /></span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item"><span className="label">IP ADDRESS</span><span className="value" style={{ color: 'white' }}>{box.ip_address || '-'}</span></div>
            <Divider className="card-divider" />
            <div className="detail-item"><span className="label"><ClockCircleOutlined /> UPTIME</span><span className="value">{box.uptime || '-'}</span></div>
            <Divider className="card-divider" />
            <div className="detail-item"><span className="label"><HistoryOutlined /> LAST SEEN</span><span className="value" style={{ color: '#94a3b8' }}>{formatLastSeen(box.last_seen)}</span></div>
          </Card>
        </Col>
        
        <Col span={8}>
          <Card title={<span style={{ color: 'white' }}><DashboardOutlined /> System</span>} bordered={false} style={cardStyle}>
            <div className="detail-item">
              <span className="label">STATUS</span>
              <span style={{ color: box.status === 'online' ? '#34d399' : '#ef4444', fontWeight: 'bold' }}>{box.status.toUpperCase()}</span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item">
              <span className="label">CPU TEMP</span>
              <span className="value" style={{ color: getTempColor(box.cpu_temp) }}>{box.cpu_temp || '-'}</span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item">
              <span className="label">CPU LOAD</span>
              <span className="value" style={{ color: getLoadColor(box.cpu_load) }}>{box.cpu_load || '-'}%</span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item">
              <span className="label">RAM</span>
              <span className="value" style={{ color: getRamColor(box.ram_usage) }}>{box.ram_usage || '-'}</span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item">
              <span className="label">DISK</span>
              <span className="value" style={{ color: getDiskColor(box.disk_usage) }}>{box.disk_usage || '-'}</span>
            </div>
          </Card>
        </Col>

        <Col span={8}>
          <Card title={<span style={{ color: 'white' }}><SettingOutlined /> Screen & Software</span>} bordered={false} style={cardStyle}>
            {/* NOVO: PRIKAZ PLEJLISTE */}
            <div className="detail-item">
              <span className="label"><OrderedListOutlined /> ACTIVE PLAYLIST</span>
              <span className="value" style={{ color: '#facc15', fontWeight: 'bold' }}>{box.current_playlist || 'No Playlist'}</span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item edit-hover" onClick={() => setIsSettingsModalVisible(true)}>
              <span className="label">RESOLUTION</span>
              <span className="value" style={{ color: 'white' }}>{box.resolution || '-'} <EditOutlined style={{ fontSize: '10px' }} /></span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item edit-hover" onClick={() => setIsSettingsModalVisible(true)}>
              <span className="label">ROTATION</span>
              <span className="value" style={{ color: 'white' }}>{box.rotation === '1' ? '90°' : box.rotation === '2' ? '180°' : box.rotation === '3' ? '270°' : '0°'} <EditOutlined style={{ fontSize: '10px' }} /></span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item">
              <span className="label"><AppstoreOutlined /> SERVICE APK</span>
              <span className="value" style={{ color: 'white' }}>{box.service_apk_version || '-'}</span>
            </div>
            <Divider className="card-divider" />
            <div className="detail-item">
              <span className="label"><AppstoreOutlined /> PLAYER APK</span>
              <span className="value" style={{ color: 'white' }}>{box.player_apk_version || '-'}</span>
            </div>
          </Card>
        </Col>
      </Row>

      <EditBoxModal isVisible={isNameModalVisible} onOk={handleSave} onCancel={() => setIsNameModalVisible(false)} updating={updating} title="Change Name & Location">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <label className="modal-label">BOX NAME</label>
              <Input value={editData.box_name} onChange={(e) => setEditData({...editData, box_name: e.target.value})} className="modal-input" />
            </Col>
            <Col span={12}>
              <label className="modal-label">LOCATION</label>
              <Input value={editData.location} onChange={(e) => setEditData({...editData, location: e.target.value})} className="modal-input" />
            </Col>
          </Row>
        </div>
      </EditBoxModal>

      <EditBoxModal isVisible={isSettingsModalVisible} onOk={handleSave} onCancel={() => setIsSettingsModalVisible(false)} updating={updating} title="Screen Settings">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <label className="modal-label">RESOLUTION</label>
              <Select 
                value={editData.resolution} 
                onChange={(val) => setEditData({...editData, resolution: val})} 
                style={{ width: '100%' }}
                dropdownStyle={{ background: '#1e293b' }}
              >
                <Option value="1920x1080">1920x1080</Option>
                <Option value="1280x720">1280x720</Option>
                <Option value="860x688">860x688</Option>
                <Option value="860x516">860x516</Option>
              </Select>
            </Col>
            <Col span={12}>
              <label className="modal-label">ROTATION</label>
              <Select 
                value={editData.rotation} 
                onChange={(val) => setEditData({...editData, rotation: val})} 
                style={{ width: '100%' }}
                dropdownStyle={{ background: '#1e293b' }}
              >
                <Option value="0">0° (Landscape)</Option>
                <Option value="1">90° (Portrait)</Option>
                <Option value="2">180° (Landscape Rev)</Option>
                <Option value="3">270° (Portrait Rev)</Option>
              </Select>
            </Col>
          </Row>
        </div>
      </EditBoxModal>

      <style>{`
        .detail-item { display: flex; justify-content: space-between; align-items: center; padding: 2px 0; }
        .edit-hover:hover { background: #334155; border-radius: 4px; cursor: pointer; }
        .label { color: #64748b; font-size: 11px; font-weight: bold; text-transform: uppercase; }
        .value { color: white; font-weight: 500; font-size: 13px; }
        .card-divider { margin: 8px 0 !important; border-color: #334155 !important; }
        .modal-label { color: #94a3b8; display: block; margin-bottom: 8px; font-size: 11px; }
        .modal-input { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
        .ant-select-selector { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
        .ant-select-arrow { color: #94a3b8 !important; }
        .ant-card-body { padding: 16px !important; }
        .ant-select-item { color: #94a3b8 !important; }
        .ant-select-item-option-selected { background: #334155 !important; color: white !important; }
        .ant-select-item-option-active { background: #334155 !important; color: white !important; }
      `}</style>
    </div>
  );
};

export default ScreenDetails;